import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { getTreatmentCodeByCountry } from "@lyfegen-platform/utils";
import { useFieldValue } from "formik";
import { useRecoilValue } from "recoil";
import * as Yup from "yup";
import { useScopes } from "../hooks/useScopes";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { combinePaths } from "../models/flexible/utils";
import { Brand, Preparation } from "../models/generated/graphql";
import { IDrawerData, recordsRightDrawerState } from "../store/lyfe-data-table";
import { getScopeTreatments } from "../utils/agreement-utils";
import { formatTreatment } from "../utils/base-agreement-formatters";
import { distinctBy } from "../utils/distinct-by";
import { getPathPrefix } from "../utils/get-path-prefix";
import { GeneralFieldController } from "./general-field-controller";
import { GET_SCOPES, whereDrugs } from "./queries";

export class ProductIdFieldController extends GeneralFieldController {
	getInitialRequestComponents(): InitialRequestConfig {
		return {
			queries: [GET_SCOPES],
			variables: { where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId) },
		};
	}

	override useControlProps(path: string): FlexControlProps {
		const recordState = useRecoilValue(recordsRightDrawerState);
		const { selectedScopes, allScopes } = useScopes(this.parent.context);
		const [recordPreparation] = useFieldValue(combinePaths(getPathPrefix(path, 1), AttributeIdentifiers.TreatmentPack));
		const isTreatmentPackExist = this.parent.fieldControllers.some(
			(controller) => controller.schema.attribute.identifier === AttributeIdentifiers.TreatmentPack
		);
		const [brand] = useFieldValue(AttributeIdentifiers.BrandName) as Array<Brand>;

		if (recordState && (recordState as unknown as IDrawerData).preparationId) {
			return {
				controlType: "typeahead",
				getOptionLabel: () => (recordState as unknown as IDrawerData).preparationId,
			};
		}

		const isPreparationMatched = (preparation: Preparation) =>
			(!recordPreparation && !brand) || (brand ? preparation.BrandName === brand.Name : preparation.id === recordPreparation?.id);
		const relevantTreatments = (selectedScopes.length ? selectedScopes : allScopes)
			.flatMap((treatmentSet) => getScopeTreatments(treatmentSet))
			.filter((treatment) => isPreparationMatched(treatment.regulatedBy));

		const distinctPreparations = distinctBy(relevantTreatments, (treatment) => treatment.Code);
		const distinctPreparationsWithFormattedName = distinctPreparations.map((treatment) => ({
			...treatment,
			Name: formatTreatment(treatment),
			CodeByCountryWithPackName: `${getTreatmentCodeByCountry(treatment)} (${formatTreatment(treatment)})`,
		}));

		return {
			controlType: "typeahead",
			property: "Code",
			optionLabel: "CodeByCountryWithPackName",
			disabled: isTreatmentPackExist && !recordPreparation,
			options: distinctPreparationsWithFormattedName,
		};
	}

	protected override createFieldValidationRuleByDataType(): Yup.AnySchema {
		return Yup.object().nullable();
	}
}
